/**
 * HeartfeltAI Site Gradients
 * Structured to work with the shared component system
 * Supports light/dark mode variants
 */

export const siteGradients = {
  // Layout Elements
  layout: {
    header: {
      light: "bg-gradient-to-r from-primary-50 via-primary-100 to-white",
      dark: "bg-gradient-to-r from-primary-900 via-primary-800 to-gray-900",
    },
    footer: {
      light: "bg-gradient-to-r from-secondary-200 to-secondary-300",
      dark: "bg-gradient-to-r from-secondary-900 to-black",
    },
    main: {
      light: "bg-gradient-to-br from-white via-primary-50/30 to-accent-50/20",
      dark: "bg-gradient-to-br from-gray-900 via-primary-900/50 to-accent-900/30",
    },
  },

  // UI Components
  components: {
    // Buttons
    button: {
      light:
        "bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700",
      dark: "bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800",
    },
    buttonSecondary: {
      light:
        "bg-gradient-to-r from-secondary-500 to-secondary-600 hover:from-secondary-600 hover:to-secondary-700",
      dark: "bg-gradient-to-r from-secondary-600 to-secondary-700 hover:from-secondary-700 hover:to-secondary-800",
    },

    // Cards
    card: {
      light: "bg-gradient-to-br from-white via-primary-50/50 to-primary-100/30",
      dark: "bg-gradient-to-br from-gray-800 via-primary-900/50 to-primary-800/30",
    },
    cardHover: {
      light: "bg-gradient-to-br from-primary-100 to-primary-200",
      dark: "bg-gradient-to-br from-primary-800 to-primary-700",
    },

    // Accent variations for Cards
    accentSubtle: {
      light: "bg-gradient-to-br from-accent-50 to-accent-100",
      dark: "bg-gradient-to-br from-accent-900/20 to-accent-800/20",
    },
    accentLight: {
      light: "bg-gradient-to-br from-accent-100 to-accent-200",
      dark: "bg-gradient-to-br from-accent-800/30 to-accent-700/30",
    },
    accentMedium: {
      light: "bg-gradient-to-br from-accent-200 to-accent-300",
      dark: "bg-gradient-to-br from-accent-700/50 to-accent-600/50",
    },

    // Sections
    section: {
      light: "bg-gradient-to-r from-white to-gray-50",
      dark: "bg-gradient-to-r from-gray-900 to-gray-800",
    },
    sectionLight: {
      light: "bg-gradient-to-r from-primary-50/60 to-primary-100/60",
      dark: "bg-gradient-to-r from-primary-900/30 to-primary-800/30",
    },
    sectionMedium: {
      light: "bg-gradient-to-r from-primary-100 to-primary-200",
      dark: "bg-gradient-to-r from-primary-800/70 to-primary-700/70",
    },

    // Glassmorphism effects
    glass: {
      light: "bg-gradient-to-br from-white/10 to-white/5",
      dark: "bg-gradient-to-br from-black/10 to-black/5",
    },
    glassPrimary: {
      light: "bg-gradient-to-br from-primary-500/10 to-primary-600/5",
      dark: "bg-gradient-to-br from-primary-600/10 to-primary-700/5",
    },
    glassSecondary: {
      light: "bg-gradient-to-br from-secondary-500/10 to-secondary-600/5",
      dark: "bg-gradient-to-br from-secondary-600/10 to-secondary-700/5",
    },
  },

  // Special Gradients
  special: {
    // Hero gradients
    heroWarm: {
      light: "bg-gradient-to-br from-primary-500 via-primary-400 to-accent-300",
      dark: "bg-gradient-to-br from-primary-600 via-primary-500 to-accent-400",
    },
    heroSunset: {
      light: "bg-gradient-to-br from-primary-600 via-caution to-accent-400",
      dark: "bg-gradient-to-br from-primary-700 via-caution to-accent-500",
    },
    heroGlow: {
      light:
        "bg-gradient-to-br from-primary-400 via-primary-300 to-secondary-200",
      dark: "bg-gradient-to-br from-primary-500 via-primary-400 to-secondary-300",
    },

    // Data visualization
    dataFlow: {
      light: "bg-gradient-to-r from-secondary-400 via-calm to-growth",
      dark: "bg-gradient-to-r from-secondary-500 via-calm to-growth",
    },
    trustGradient: {
      light: "bg-gradient-to-r from-accent-400 via-accent-500 to-primary-400",
      dark: "bg-gradient-to-r from-accent-500 via-accent-600 to-primary-500",
    },
    innovationGradient: {
      light: "bg-gradient-to-r from-secondary-500 via-calm to-secondary-700",
      dark: "bg-gradient-to-r from-secondary-600 via-calm to-secondary-800",
    },

    // Radial gradients
    radialWarmth: {
      light: "bg-radial from-primary-400/20 via-primary-300/10 to-transparent",
      dark: "bg-radial from-primary-500/20 via-primary-400/10 to-transparent",
    },
    radialTech: {
      light:
        "bg-radial from-secondary-400/20 via-secondary-300/10 to-transparent",
      dark: "bg-radial from-secondary-500/20 via-secondary-400/10 to-transparent",
    },
    radialSoft: {
      light: "bg-radial from-accent-400/20 via-accent-300/10 to-transparent",
      dark: "bg-radial from-accent-500/20 via-accent-400/10 to-transparent",
    },
  },

  // Feature-specific Gradients
  features: {
    // Hero sections
    hero: {
      light:
        "bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-600",
      dark: "bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-700",
    },
    heroSecondary: {
      light:
        "bg-gradient-to-br from-secondary-500 via-secondary-600 to-primary-600",
      dark: "bg-gradient-to-br from-secondary-600 via-secondary-700 to-primary-700",
    },
    heroAccent: {
      light: "bg-gradient-to-br from-accent-400 via-accent-500 to-primary-500",
      dark: "bg-gradient-to-br from-accent-500 via-accent-600 to-primary-600",
    },

    // Feature cards
    featureIcon: {
      light: "bg-gradient-to-br from-primary-50 to-primary-100",
      dark: "bg-gradient-to-br from-gray-700 to-gray-600",
    },
    featureCard: {
      light: "bg-gradient-to-br from-gray-50 to-gray-100",
      dark: "bg-gradient-to-br from-gray-800 to-gray-700",
    },
    featureCardHover: {
      light: "bg-gradient-to-br from-primary-50 to-primary-100",
      dark: "bg-gradient-to-br from-gray-700 to-gray-600",
    },
  },

  // Overlay effects
  overlays: {
    subtle: {
      light: "bg-gradient-to-b from-black/5 to-transparent",
      dark: "bg-gradient-to-b from-black/10 to-transparent",
    },
    medium: {
      light: "bg-gradient-to-b from-black/10 to-transparent",
      dark: "bg-gradient-to-b from-black/20 to-transparent",
    },
    strong: {
      light: "bg-gradient-to-b from-black/20 to-transparent",
      dark: "bg-gradient-to-b from-black/30 to-transparent",
    },
  },

  // Text gradients
  text: {
    primary: {
      light:
        "bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent",
      dark: "bg-gradient-to-r from-primary-400 to-primary-500 bg-clip-text text-transparent",
    },
    secondary: {
      light:
        "bg-gradient-to-r from-secondary-600 to-secondary-700 bg-clip-text text-transparent",
      dark: "bg-gradient-to-r from-secondary-400 to-secondary-500 bg-clip-text text-transparent",
    },
  },
};

// Helper function to get gradient
export const getGradient = (gradientPath) => {
  const parts = gradientPath.split(".");
  let gradient = siteGradients;

  for (const part of parts) {
    gradient = gradient?.[part];
    if (!gradient) {
      console.warn(`Gradient path "${gradientPath}" not found`);
      return "";
    }
  }

  // If it's a light/dark object, return based on current theme
  if (gradient.light && gradient.dark) {
    const isDark = document.documentElement.classList.contains("dark");
    return gradient[isDark ? "dark" : "light"];
  }

  return gradient;
};
